<template>
  <div>
    <div class="flex flex-col space-y-6">
      <h2 :class="titlePosition === 'left' ? 'text-start' : 'text-center'" class="text-2xl font-bold mt-2 mb-5">
        {{ $t("common.howItWorks") }}
      </h2>
      <div class="relative min-w-0 w-full" v-if="filteredServices.length > 1">
        <button
          type="button"
          class="absolute left-0 top-1/2 z-[1] mx-2.5 flex h-[30px] w-[30px] -translate-y-1/2 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-black hover:enabled:text-[#090f207f] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:!hidden"
          :disabled="!canScrollTabsPrev"
          aria-label="Previous service"
          @click.stop.prevent="scrollTabsBy(-1)"
        >
          <svg class="h-7 w-7 fill-current" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
          </svg>
        </button>

        <div
          id="how-it-works__tabs"
          ref="tabsRef"
          class="relative mx-10 flex min-w-0 w-[calc(100%-5rem)] items-center gap-5 overflow-x-auto scroll-smooth pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory md:mx-0 md:w-full"
          role="tablist"
          @scroll="updateTabsScrollState"
        >
          <button
            v-for="(service, serviceIdx) in filteredServices"
            :key="service.id ?? service.slug ?? serviceIdx"
            type="button"
            role="tab"
            :aria-selected="serviceIdx === currentServiceIndex"
            :class="
              serviceIdx === currentServiceIndex
                ? 'bg-secondary border-secondary text-white'
                : 'border bg-transparent text-inherit'
            "
            class="snap-start shrink-0 whitespace-nowrap rounded-full border border-[#00A1AA99] px-4 py-2 text-sm font-semibold transition-colors duration-300 md:text-base"
            :id="'how-it-works-tab-' + slideStableKey(service, serviceIdx)"
            @click="navigateTo(serviceIdx)"
          >
            <!-- Spacer-only fallback here: thin carousel chips vs branded SVG in ServiceTypeBlock card list. -->
            <div class="flex items-center">
              <CommonSafeNuxtImg
                loading="lazy"
                :src="service.icon?.permalink"
                :class="serviceIdx === currentServiceIndex ? 'text-white' : 'text-primary'"
                class="mr-3 h-7 w-7 shrink-0 group-hover:text-dark-blue"
                alt=""
              >
                <template #fallback>
                  <div class="mr-3 h-7 w-7 shrink-0" aria-hidden="true" />
                </template>
              </CommonSafeNuxtImg>
              <span>{{ service.title }}</span>
            </div>
          </button>
        </div>

        <button
          type="button"
          class="absolute right-0 top-1/2 z-[1] mx-2.5 flex h-[30px] w-[30px] -translate-y-1/2 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-black hover:enabled:text-[#090f207f] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:!hidden"
          :disabled="!canScrollTabsNext"
          aria-label="Next service"
          @click.stop.prevent="scrollTabsBy(1)"
        >
          <svg class="h-7 w-7 fill-current" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
          </svg>
        </button>
      </div>
      <div class="w-full" :class="inModel === true ? 'md:w-5/6' : 'md:w-3/5'" v-if="filteredServices.length > 1">
        <p class="text-sm md:text-base">{{ currentService?.intro_text }}</p>
      </div>
      <div class="relative min-w-0 overflow-x-hidden">
        <div
          class="pointer-events-none absolute left-0 top-0 z-[2] h-full w-10 bg-gradient-to-r from-[#f7f7f7] to-transparent"
          :style="{ opacity: fadeLeftOpacity }"
        ></div>
        <div
          class="pointer-events-none absolute right-0 top-0 z-[2] h-full w-10 bg-gradient-to-l from-[#f7f7f7] to-transparent"
          :style="{ opacity: fadeRightOpacity }"
        ></div>

        <div
          ref="stepsRef"
          class="relative flex min-w-0 w-full items-start gap-10 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory md:snap-none"
          id="how-it-works__steps"
          @scroll="handleScrollChange"
        >
          <div
            v-for="(step, index) in currentService?.steps ?? []"
            :key="step.id ?? index"
            class="snap-start shrink-0"
            :class="inModel === true ? 'w-60' : 'md:w-72 w-60'"
          >
            <div class="rounded-sm mb-1" :class="inModel === true ? 'w-60 h-48' : 'md:w-72 md:h-60 w-60 h-48'">
              <CommonSafeNuxtImg
                loading="lazy"
                :src="step.image?.permalink"
                :alt="step.title"
                class="object-cover object-center h-full w-full"
              >
                <template #fallback>
                  <div class="h-full w-full bg-gray-200" aria-hidden="true" />
                </template>
              </CommonSafeNuxtImg>
            </div>
            <div class="flex flex-col space-y-3">
              <span class="font-bold text-primary text-sm uppercase tracking-[0.98px]">
                {{ $t("common.howItWorksStep", { step: index + 1 }) }}
              </span>
              <span class="font-semibold">{{ step.title }}</span>
              <p class="text-sm">{{ step.intro_text }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-center mt-16 mb-10">
      <CommonRangeSlider
        name="carousselle-range"
        id="how-it-works-range"
        :min="0"
        :max="100"
        :value="scrollValue"
        @input="handleRangeChange"
      />
    </div>

    <div class="flex justify-center mt-16 lg:mt-20 xl:mt-24 mb-0 md:mb-4" v-if="displayCtaButton === true && cta">
      <NuxtLinkLocale :locale="switchBlogLocale()" :to="cta.to" class="flex w-full md:w-auto">
        <BaseButton class="md:w-80 w-full h-16 mx-0 text-lg" :text="cta.text"></BaseButton>
      </NuxtLinkLocale>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  services: {
    type: Object,
    default: null,
  },
  inModel: {
    type: Boolean,
    default: false,
  },
  displayCtaButton: {
    type: Boolean,
    default: false,
  },
  titlePosition: {
    type: String,
    default: "center",
  },
  defaultService: {
    type: Object,
    required: false,
  },
});

const { t } = useI18n();
const route = useRoute();
const globalStore = useGlobalStore();
const scrollValue = ref(0);
const availableServices = ref([]);
const tabsRef = ref(null);
const stepsRef = ref(null);
let tabsObserver = null;
let snapRestoreTimer = null;

const { data: serviceList } = await useHowItWorkServices();
availableServices.value = Array.isArray(serviceList.value?.data) ? serviceList.value.data : [];

const filteredServices = computed(() =>
  availableServices.value.filter((service) => {
    const matchesRequested = !props.services || (props.services.length > 0 && props.services.includes(service.slug));
    if (!matchesRequested) return false;
    // Chip list is CMS how-it-works; home tests are gated by /service-types.
    if (service.slug === "home-test-kit" && !globalStore.isHomeTestEnabled) return false;
    return true;
  })
);

/** Stable identity when CMS omits `id` (matches :key / DOM id). */
const slideStableKey = (service, index) => service?.id ?? service?.slug ?? index;

const resolveInitialIndex = () => {
  const services = filteredServices.value;
  if (!services.length) {
    return 0;
  }
  const fromDefault = services.findIndex((x) => x.slug === props.defaultService?.slug);
  return fromDefault >= 0 ? fromDefault : 0;
};

// CTA label + destination for the button below the steps carousel are
// driven by the currently selected service (chip). Unknown slugs return
// null so the template guard hides the button instead of mis-routing.
const CTA_BY_SERVICE = {
  "clinic-test": { to: "/clinics", textKey: "common.btnFindClinic" },
  "home-test-kit": { to: "/tests/home-tests", textKey: "common.btnFindHomeTests" },
  "instant-test": { to: "/get-started", textKey: "common.btnGetStarted" },
  "everywhere-nurse-service": { to: "/get-started", textKey: "common.btnGetStarted" },
};

const normalizePath = (path) => path?.replace(/\/$/, "") || "/";

const currentServiceIndex = ref(resolveInitialIndex());

const currentService = computed(() => filteredServices.value[currentServiceIndex.value] ?? null);

const cta = computed(() => {
  const slug = currentService.value?.slug;
  if (!slug) return null;
  if (slug === "home-test-kit" && !globalStore.isHomeTestEnabled) return null;

  const config = CTA_BY_SERVICE[slug];
  if (!config) return null;

  const currentPath = normalizePath(trimLocaleFromPathname(route.path));
  if (currentPath === normalizePath(config.to)) return null;

  return { to: config.to, text: t(config.textKey) };
});

const clampedScroll = computed(() => Math.min(Math.max(scrollValue.value || 0, 0), 100));
const fadeLeftOpacity = computed(() => clampedScroll.value / 100);
const fadeRightOpacity = computed(() => (100 - clampedScroll.value) / 100);

const canScrollTabsPrev = ref(false);
const canScrollTabsNext = ref(false);

const updateTabsScrollState = () => {
  const tabs = tabsRef.value;
  if (!tabs) {
    canScrollTabsPrev.value = false;
    canScrollTabsNext.value = false;
    return;
  }
  const maxScroll = tabs.scrollWidth - tabs.clientWidth;
  canScrollTabsPrev.value = tabs.scrollLeft > 2;
  canScrollTabsNext.value = maxScroll > 2 && tabs.scrollLeft < maxScroll - 2;
};

const observeTabs = () => {
  const tabs = tabsRef.value;
  tabsObserver?.disconnect();
  if (!tabs || typeof ResizeObserver === "undefined") {
    return;
  }
  tabsObserver = new ResizeObserver(() => updateTabsScrollState());
  tabsObserver.observe(tabs);
  [...tabs.children].forEach((child) => tabsObserver.observe(child));
};

const refreshTabsLayout = async () => {
  if (!import.meta.client) {
    return;
  }
  await nextTick();
  observeTabs();
  updateTabsScrollState();
  requestAnimationFrame(updateTabsScrollState);
};

const scrollTabsBy = (direction) => {
  const tabs = tabsRef.value;
  if (!tabs) {
    return;
  }
  // Slide by roughly one chip width (matches previous carousel peek behavior).
  const amount = Math.max(tabs.clientWidth * 0.65, 160);
  tabs.scrollBy({ left: direction * amount, behavior: "smooth" });
};

const scrollTabIntoView = (slideIndex) => {
  const tabs = tabsRef.value;
  const tab = tabs?.children?.[slideIndex];
  if (!tabs || !tab) {
    return;
  }
  const target = tab.offsetLeft - (tabs.clientWidth - tab.offsetWidth) / 2;
  tabs.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
};

const handleRangeChange = (value) => {
  const stepsContainer = stepsRef.value;
  if (!stepsContainer) {
    return;
  }
  const maxScroll = stepsContainer.scrollWidth - stepsContainer.clientWidth;
  const scrollPercentage = Number(value ?? scrollValue.value) / 100;
  // Snap-mandatory on mobile would otherwise fight the range thumb.
  stepsContainer.style.scrollSnapType = "none";
  stepsContainer.scrollLeft = maxScroll * scrollPercentage;
  clearTimeout(snapRestoreTimer);
  snapRestoreTimer = setTimeout(() => {
    stepsContainer.style.scrollSnapType = "";
  }, 180);
};

const handleScrollChange = (e) => {
  /**
   * Steps container.
   * @type {HTMLDivElement}
   */
  const container = e.target;

  const total = container.scrollWidth - container.offsetWidth;
  scrollValue.value = total > 0 ? (container.scrollLeft / total) * 100 : 0;
};

const navigateTo = async (slideIndex) => {
  if (typeof slideIndex !== "number" || slideIndex < 0 || slideIndex >= filteredServices.value.length) {
    return;
  }
  currentServiceIndex.value = slideIndex;
  scrollValue.value = 0;
  await nextTick();
  scrollTabIntoView(slideIndex);
  const stepsContainer = stepsRef.value;
  if (stepsContainer) {
    stepsContainer.scrollLeft = 0;
  }
};

watch(
  () => filteredServices.value.map((service) => service?.id ?? service?.slug),
  async () => {
    if (!import.meta.client) {
      return;
    }
    if (currentServiceIndex.value >= filteredServices.value.length) {
      currentServiceIndex.value = Math.max(0, filteredServices.value.length - 1);
    }
    await refreshTabsLayout();
    scrollTabIntoView(currentServiceIndex.value);
  },
  { flush: "post" }
);

onMounted(() => {
  refreshTabsLayout().then(() => {
    scrollTabIntoView(currentServiceIndex.value);
  });
  window.addEventListener("resize", updateTabsScrollState);
});

onBeforeUnmount(() => {
  tabsObserver?.disconnect();
  clearTimeout(snapRestoreTimer);
  window.removeEventListener("resize", updateTabsScrollState);
});
</script>
